// TaskView.js
import React, { useState, useEffect, useCallback, Suspense } from "react";
import Card from "../../Components/Card/Card";
import "./TaskView.css";
import WorkOrderForm from "../../Components/WorkOrderForm/work-order-form";
import { fetchEmployees, fetchTasksByDate, updateTask, filterTasksForActiveCustomers } from "../../Utils/api";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import PopupGeneric from '../../Components/PopupGeneric/pop-up-generic';
import { Tabs, Tab } from '@mui/material';
import WorkOrdersPage from '../workord-tab/workorderpage.jsx';
import { useAuth } from "../../Auth/AuthContext";
import LoadingSpinner from "../../Components/LoadingSpinner/LoadingSpinner";


const TaskView = () => {
  const today = new Date();

   const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (event, newValue) => {
    if (newValue === 0) {
    setTabIndex(0);
  } else if (newValue === 1) {
     setTabIndex(1);

  }
  };

  // const companyId = 2; // hardcoded company id for now
  // State variables
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [activeDate, setActiveDate] = useState(today.getDate());
  const [filter, setFilter] = useState("All");
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [isCancelPopupVisible, setCancelPopupVisible] = useState(false);
  const [taskToCancel, setTaskToCancel] = useState(null);
  const { companyId } = useAuth();

  function getStartOfWeek(date) {
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const diff = date.getDate() - day; // Subtract days to get to Sunday
    return new Date(date.setDate(diff));
  }

  const buildIsoDateString = (year, month, date) => {
    const mm = String(month + 1).padStart(2, "0");
    const dd = String(date).padStart(2, "0");
    const yyyy = String(year);
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchTasks = useCallback(async (year, month, day) => {
    try {
      const isoDate = buildIsoDateString(year, month, day);
      const response = await fetchTasksByDate(companyId, isoDate);
      if (Array.isArray(response)) {
        // Filter tasks to only include active customers
        const activeTasks = filterTasksForActiveCustomers(response);

        const technicianList = await fetchEmployees(companyId, null, "Technician");
        const formattedTasks = activeTasks.map((item) => ({
          customerName: `${item.customer.custFirstName} ${item.customer.custLastName}`,
          address: `${item.customer.custAddress}, ${item.customer.custCity}, ${item.customer.custState} ${item.customer.custZip}`,
          assigneeName: `${item.employee.firstName} ${item.employee.lastName}`,
          assignee: item.employee,
          status: item.taskDetails.taskStatus,
          workOrderLog: item.taskDetails,
          workOrderTaskDate: item.taskDetails.workOrderTaskDueDate,
          workOrderTaskComment: item.taskDetails.srvcCmnt,
          employeeId: item.employee.employeeId,
          customerId: item.customer.customerId,
          employeeList: technicianList,
        }));
        setTasks(formattedTasks);

      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error("Failed to load tasks", error);
      setTasks([]);
    }
  }, [companyId]);

  // Handle date input change
  const handleDateChange = (event) => {
    const newDate = new Date(event.target.value);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
    setActiveDate(newDate.getDate());
  };

  useEffect(() => {
    fetchTasks(currentYear, currentMonth, activeDate);
  }, [currentMonth, currentYear, activeDate, fetchTasks]);

  const filteredTasks = tasks.filter((task) => {
    if (filter === "All") return true;
    if (filter === "In-Progress") return task.status === "In-Progress";
    if (filter === "Not Started") return task.status !== "In-Progress" && task.status !== "Completed" && task.status !== "Cancelled";
    if (filter === "Completed") return task.status === "Completed";
    if (filter === "Cancelled") return task.status === "Cancelled";
    return false;
  });

  const getStatusColor = (status) => {
    const statusColors = {
      "In-Progress": "#FFA500", // Orange
      "Not Started": "#0066CC", // Blue
      "Completed": "#4CAF50", // Green
      "Cancelled": "#F44336", // Red
    };
    return statusColors[status] || "#002244"; // Default color
  };

  const renderCalendarDays = () => {
    const startOfWeek = getStartOfWeek(new Date(currentYear, currentMonth, activeDate));
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const dayOfWeek = date.getDay();
      const formattedDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`; // MM/DD/YYYY format
      const isActive =
        date.getDate() === activeDate &&
        date.getMonth() === currentMonth &&
        date.getFullYear() === currentYear;

      days.push(
        <div
          key={i}
          className={`calendar-day ${isActive ? "active-day" : ""}`}
          onClick={() => {
            setActiveDate(date.getDate());
            setCurrentMonth(date.getMonth());
            setCurrentYear(date.getFullYear());
          }}
        >
          <p className="date-number">{formattedDate}</p>
          <p className="day-name">{["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dayOfWeek]}</p>
        </div>
      );
    }
    return days;
  };
  const renderFilterButtons = () => {
    const filters = ["All", "Not Started", "In-Progress", "Completed", "Cancelled"];

    // Calculate counts for each filter
    const getFilterCount = (status) => {
      if (status === "All") return tasks.length;
      if (status === "In-Progress") return tasks.filter(task => task.status === "In-Progress").length;
      if (status === "Not Started") return tasks.filter(task => task.status !== "In-Progress" && task.status !== "Completed" && task.status !== "Cancelled").length;
      if (status === "Completed") return tasks.filter(task => task.status === "Completed").length;
      if (status === "Cancelled") return tasks.filter(task => task.status === "Cancelled").length;
      return 0;
    };

    return (
      <div className="filter-buttons">
        {filters.map((status) => (
          <button
            key={status}
            className={`filter-button ${filter === status ? "active" : ""}`}
            onClick={() => {

                setFilter(status);

            }}
          >
            {status} ({getFilterCount(status)})
          </button>
        ))}
        <input
          type="date"
          value={new Date(currentYear, currentMonth, activeDate).toISOString().split("T")[0]}
          onChange={handleDateChange}
          style={{
            marginLeft: "auto",
            padding: "8px 12px",
            borderRadius: "20px",
            border: "1px solid #ccc",
            fontSize: "14px",
            backgroundColor: "#fff",
            cursor: "pointer",
            outline: "none"
          }}
        />
      </div>
    );
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentYear, currentMonth, activeDate - 7);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
    setActiveDate(newDate.getDate());
  };

  // Navigate to the next week
  const goToNextWeek = () => {
    const newDate = new Date(currentYear, currentMonth, activeDate + 7);
    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());
    setActiveDate(newDate.getDate());
  };


  const onConfirmCancel = async () => {
    if (!taskToCancel) return;

    const updatedData = {
      ...taskToCancel.workOrderLog,
      logId: taskToCancel.workOrderLog.logId,
      taskStatus: "Cancelled",
      evidences: [],
    };
    try {
      await updateTask(updatedData);
      fetchTasks(currentYear, currentMonth, activeDate);
    } catch (error) {
      console.error("Error cancelling work order:", error);
    }
    setCancelPopupVisible(false);
    setTaskToCancel(null);
  };

  const onCancelPopupClose = () => {
    setCancelPopupVisible(false);
    setTaskToCancel(null);
  };

  const onEdit = (task) => {
    setSelectedTask(task);
    setModalOpen(true);
  };

  const onCancelClick = (task) => {
    setTaskToCancel(task);
    setCancelPopupVisible(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedTask(null);
  };

  const handleFormSubmit = async (updatedTask) => {
    const originalLog = selectedTask.workOrderLog;

    const formattedUpdatedDate = updatedTask.date
      ? updatedTask.date.format("YYYY-MM-DDTHH:mm:ss")
      : null;

    const originalDateOnly = originalLog.creaTs ? originalLog.creaTs.split("T")[0] : null;
    const updatedDateOnly = formattedUpdatedDate ? formattedUpdatedDate.split("T")[0] : null;

    const payload = {
      // taskId may be null for a virtual occurrence; server materializes via wrkOdrId + occurrenceDate.
      taskId: originalLog.taskId ?? null,
      occurrenceDate: originalLog.occurrenceDate ?? null,
      logId: originalLog.logId,
      wrkOdrId: originalLog.wrkOdrId,
      poolId: originalLog.poolId,
      customerId: originalLog.customerId,
      empId: updatedTask.selectedTechnician?.employeeId
        ? updatedTask.selectedTechnician.employeeId
        : originalLog.empId,
      checkIn: originalLog.checkIn,
      checkOut: originalLog.checkOut,
      outCond: originalLog.outCond,
      outWtrLevel: originalLog.outWtrLevel,
      outAlg: originalLog.outAlg,
      phLvl: originalLog.phLvl,
      chlrLvl: originalLog.chlrLvl,
      alklLvl: originalLog.alklLvl,
      stabLvl: originalLog.stabLvl,
      chemChlrTab: originalLog.chemChlrTab,
      chemChlrGrn: originalLog.chemChlrGrn,
      chemChlrLiq: originalLog.chemChlrLiq,
      chemMrtcAcd: originalLog.chemMrtcAcd,
      logCmnt: originalLog.logCmnt,
      srvcClnPmpBsk: originalLog.srvcClnPmpBsk,
      srvcBckWshMain: originalLog.srvcBckWshMain,
      srvcSkimBsk: originalLog.srvcSkimBsk,
      srvcVcum: originalLog.srvcVcum,
      srvcWallBrsh: originalLog.srvcWallBrsh,
      srvcFlrBrsh: originalLog.srvcFlrBrsh,
      srvcCmnt: updatedTask.additionalComments || originalLog.srvcCmnt,
      imgEvd: originalLog.imgEvd,
      taskStatus: originalLog.taskStatus,
      workOrderTaskDueDate: updatedTask.date,
      updtTs: new Date().toISOString(),
      evidences: []
    };


    if (updatedDateOnly && updatedDateOnly !== originalDateOnly) {
      payload.creaTs = formattedUpdatedDate;
    }
    else {
      payload.creaTs = originalLog.creaTs;
    }

    try {
      await updateTask(payload);
      fetchTasks(currentYear, currentMonth, activeDate);
      
      handleModalClose();
    } catch (error) {
      console.error("Error updating work order task:", error);
    }
  };

  return (
    <div className="task-view">
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabIndex} onChange={handleTabChange} centered>
          <Tab label="Weekly Routes" />
          <Tab label="Work Orders" />
        </Tabs>
      </Box>

      {tabIndex === 0 && (
        <div>
          <div className="calendar-container">
            <div className="calendar-header">
              <button onClick={goToPreviousWeek}>Previous Week</button>
              <h3 style={{ margin: '0 20px', color: '#002244' }}>
                {new Date(currentYear, currentMonth, activeDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <button onClick={goToNextWeek}>Next Week</button>
            </div>
            <div className="calendar-scroll">{renderCalendarDays()}</div>
          </div>

          <div className="filter-buttons">{renderFilterButtons()}</div>

          <div className="cards-grid">
            <Suspense fallback={<LoadingSpinner />}>
              {filteredTasks.map((task, index) => (
                <Card
                  key={index}
                  customerName={task.customerName}
                  address={task.address}
                  assignee={task.assigneeName}
                  status={task.status}
                  date = {task.workOrderTaskDate}
                  statusColor={getStatusColor(task.status)}
                  onCancel={() => onCancelClick(task)}
                  onEdit={() => onEdit(task)}
                />
              ))}
            </Suspense>
          </div>
        </div>
      )}
      {tabIndex === 1 && (
  <Suspense fallback={<LoadingSpinner />}>
    <WorkOrdersPage />
  </Suspense>
)}
        <div>
          <Suspense fallback={<LoadingSpinner />}>
            <PopupGeneric
              isVisible={isCancelPopupVisible}
              onClose={onCancelPopupClose}
              title="Cancel Work Order"
              content={<p>Are you sure you want to cancel this work order?</p>}
              actions={
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button
                    onClick={onConfirmCancel}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '5px',
                      backgroundColor: '#1d5ca7',
                      color: '#fff',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Yes
                  </button>
                  <button
                    onClick={onCancelPopupClose}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '5px',
                      backgroundColor: '#8f0c03',
                      border: 'none',
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    No
                  </button>
                </div>
              }
            />
          </Suspense>

          <Suspense fallback={<LoadingSpinner />}>
            <Modal open={isModalOpen} onClose={handleModalClose}>
              <Box className="task-modal-container">
                <WorkOrderForm
                  formType="task"
                  task={selectedTask}
                  onSubmit={handleFormSubmit}
                  onClose={handleModalClose}
                />
              </Box>
            </Modal>
          </Suspense>
        </div>
      
    </div>
  );
};

export default TaskView;